import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle2, Circle, AlertTriangle, Clock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const STATUS_COLORS = { completed: 'text-green-500', in_progress: 'text-gold', review: 'text-blue-400', not_started: 'text-muted-foreground/40' };
const STATUS_ICONS = { completed: CheckCircle2, in_progress: Clock, review: AlertTriangle, not_started: Circle };
const PRIORITY_DOT = { high: 'bg-red-500', medium: 'bg-foreground/30', low: 'bg-foreground/15' };

export default function SharedProjectPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/shared/project/${shareToken}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('This shared link is invalid or has been revoked.'); setLoading(false); });
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[#dd9202]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <p className="text-lg font-semibold text-white/60">{error}</p>
        <p className="text-sm text-white/30 mt-2">Contact the project owner for access.</p>
      </div>
    </div>
  );

  const grouped = {};
  for (const t of (data?.tasks || [])) {
    const s = t.status || 'not_started';
    if (!grouped[s]) grouped[s] = [];
    grouped[s].push(t);
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="shared-project-page">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <p className="text-[11px] text-[#dd9202] font-medium uppercase tracking-wider mb-2">Shared Project</p>
          <h1 className="text-2xl font-bold">{data?.name}</h1>
          {data?.description && <p className="text-sm text-white/50 mt-2">{data.description}</p>}
          <div className="flex items-center gap-6 mt-4">
            <div>
              <p className="text-2xl font-bold text-[#dd9202]">{data?.progress}%</p>
              <p className="text-[11px] text-white/30">Complete</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{data?.completed_tasks}/{data?.total_tasks}</p>
              <p className="text-[11px] text-white/30">Tasks Done</p>
            </div>
          </div>
          <Progress value={data?.progress || 0} className="h-1.5 mt-3" />
        </div>
      </div>

      {/* Tasks by status */}
      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {['in_progress', 'review', 'not_started', 'completed'].map(status => {
          const tasks = grouped[status] || [];
          if (tasks.length === 0) return null;
          const Icon = STATUS_ICONS[status] || Circle;
          return (
            <div key={status}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`h-4 w-4 ${STATUS_COLORS[status]}`} />
                <h2 className="text-sm font-semibold text-white/70">{status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</h2>
                <span className="text-[11px] text-white/30">({tasks.length})</span>
              </div>
              <div className="space-y-1">
                {tasks.map(t => (
                  <div key={t.title + t.due_date} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium}`} />
                    <span className="text-[13px] flex-1">{t.title}</span>
                    {t.assignee_names?.length > 0 && <span className="text-[11px] text-white/30">{t.assignee_names.join(', ')}</span>}
                    {t.due_date && <span className="text-[11px] text-white/20">{t.due_date.slice(0, 10)}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="border-t border-white/5 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center">
          <p className="text-[11px] text-white/20">Powered by Stellar Productions CRM</p>
        </div>
      </div>
    </div>
  );
}
